/**
 * The placement grid button (1.9.0): a 3×3 popover of the cells (top, middle, bottom × left, centre, right), plus
 * "Track default". role=dialog, so Esc closes it instead of leaving the asset.
 */
import { CUE_ALIGNS, type CueAlign } from '@prism/contracts';
import { Popover } from './Popover';

export interface PlacementGridProps {
  /** The cue's own cell; null: the track's. Several selected cues with different cells: null and `mixed`. */
  value: CueAlign | null;
  mixed?: boolean;
  /** The cell the track style gives (shown for "Track default"). */
  trackAlign: CueAlign;
  /** A cell, or null for the track default. */
  onChange(align: CueAlign | null): void;
  disabled?: boolean;
  testId?: string;
}

const ROWS = { t: 'Top', m: 'Middle', b: 'Bottom' } as const;
const COLS = { l: 'left', c: 'centre', r: 'right' } as const;
/** "Top left", "Bottom centre", … */
export const alignLabel = (a: CueAlign): string => `${ROWS[a[0] as 't' | 'm' | 'b']} ${COLS[a[1] as 'l' | 'c' | 'r']}`;

/** A small 3×3 picture of a cell (the button's face). */
export function AlignIcon({ align, faint }: { align: CueAlign | null; faint?: boolean }) {
  return (
    <span className={`align-icon ${faint ? 'faint' : ''}`} aria-hidden>
      {CUE_ALIGNS.map((a) => <i key={a} className={a === align ? 'on' : ''} />)}
    </span>
  );
}

/** The 3×3 cells, as buttons (also used by Track style…). */
export function AlignCells({ value, onPick, testId, fallback }: { value: CueAlign | null; onPick: (a: CueAlign) => void; testId?: string; /** The cell the track gives (marked while the cue has none of its own). */ fallback?: CueAlign }) {
  return (
    <div className="align-grid" role="group" aria-label="Placement">
      {CUE_ALIGNS.map((a) => (
        <button key={a} type="button" className={`align-cell ${value === a ? 'on' : ''} ${value === null && fallback === a ? 'track' : ''}`} aria-label={alignLabel(a)} aria-pressed={value === a} title={alignLabel(a)}
          data-align={a} data-testid={testId ? `${testId}-${a}` : undefined} onClick={() => onPick(a)}><i /></button>
      ))}
    </div>
  );
}

export function PlacementGrid({ value, mixed, trackAlign, onChange, disabled, testId = 'cue-grid' }: PlacementGridProps) {
  const shown = value ?? (mixed ? null : trackAlign);
  return (
    <Popover label="Placement" title={`Placement: ${mixed ? 'mixed' : value ? alignLabel(value) : `track default (${alignLabel(trackAlign).toLowerCase()})`}`}
      button={<AlignIcon align={shown} faint={!value} />} disabled={disabled} testId={testId} className="btn ghost sm icon" width={150}>
      {(close) => (
        <>
          <AlignCells value={value} fallback={mixed ? undefined : trackAlign} testId={testId} onPick={(a) => { onChange(a); close(); }} />
          <button type="button" className={`btn ghost sm ${value === null && !mixed ? 'on' : ''}`} style={{ width: '100%', marginTop: 6 }} aria-pressed={value === null && !mixed}
            data-testid={`${testId}-default`} onClick={() => { onChange(null); close(); }}>Track default</button>
        </>
      )}
    </Popover>
  );
}
